/**
 * Toast Notification Utilities
 * Standardized toast messages for consistent user feedback
 */

import { toast } from "react-hot-toast";

/**
 * Success toast for completed actions
 */
export const showSuccess = (message: string) => {
  toast.success(message, {
    duration: 3000,
    position: "top-right",
  });
};

/**
 * Error toast for failed actions
 */
export const showError = (message: string) => {
  toast.error(message, {
    duration: 5000,
    position: "top-right",
  });
};

/**
 * Info toast for neutral messages
 */
export const showInfo = (message: string) => {
  toast(message, {
    duration: 4000,
    position: "top-right",
    icon: "ℹ️",
  });
};

/**
 * Loading toast that can be dismissed
 * Returns a toast ID that can be used to dismiss it
 */
export const showLoading = (message: string = "Loading...") => {
  return toast.loading(message, {
    position: "top-right",
  });
};

/**
 * Dismiss a toast by ID
 */
export const dismissToast = (toastId: string) => {
  toast.dismiss(toastId);
};

/**
 * Dismiss all toasts
 */
export const dismissAllToasts = () => {
  toast.dismiss();
};

// ============================================
// PRESET MESSAGES FOR COMMON ACTIONS
// ============================================

export const toastMessages = {
  // Generic CRUD operations
  create: {
    success: (entity: string) => `${entity} created successfully`,
    error: (entity: string) => `Failed to create ${entity.toLowerCase()}`,
  },
  update: {
    success: (entity: string) => `${entity} updated successfully`,
    error: (entity: string) => `Failed to update ${entity.toLowerCase()}`,
  },
  delete: {
    success: (entity: string) => `${entity} deleted successfully`,
    error: (entity: string) => `Failed to delete ${entity.toLowerCase()}`,
  },
  save: {
    success: "Changes saved successfully",
    error: "Failed to save changes",
  },

  // Data operations
  export: {
    success: (count: number, entity: string) =>
      `Exported ${count} ${entity.toLowerCase()}${count !== 1 ? "s" : ""}`,
    error: (entity: string) =>
      `Failed to export ${entity.toLowerCase()}`,
  },
  import: {
    success: (count: number) => `Imported ${count} records successfully`,
    error: "Failed to import data",
  },
  copy: {
    success: "Copied to clipboard",
    error: "Failed to copy to clipboard",
  },

  // Authentication
  auth: {
    loginSuccess: "Welcome back!",
    logoutSuccess: "Logged out successfully",
    passwordChanged: "Password changed successfully",
    resetEmailSent: "Password reset email sent",
  },

  // Payments
  payment: {
    success: "Payment processed successfully",
    error: "Payment failed. Please try again.",
    refundSuccess: "Refund processed successfully",
    refundError: "Failed to process refund",
  },

  // Applications
  application: {
    submitted: "Application submitted successfully",
    approved: "Application approved",
    denied: "Application denied",
    waitlisted: "Application waitlisted",
  },

  // Invoices
  invoice: {
    generated: "Invoice generated successfully",
    sent: "Invoice sent successfully",
    voided: "Invoice voided",
  },

  // Status changes
  status: {
    activated: (entity: string) => `${entity} activated`,
    deactivated: (entity: string) => `${entity} deactivated`,
    archived: (entity: string) => `${entity} archived`,
  },

  // Network errors
  network: {
    offline: "You appear to be offline. Please check your connection.",
    timeout: "Request timed out. Please try again.",
    serverError: "Server error. Please try again later.",
  },

  // Validation
  validation: {
    required: "Please fill in all required fields",
    invalid: "Please check your input and try again",
  },
} as const;

// ============================================
// SHORTHAND FUNCTIONS FOR COMMON OPERATIONS
// ============================================

/**
 * Show success toast for entity creation
 */
export const showCreateSuccess = (entity: string) => {
  showSuccess(toastMessages.create.success(entity));
};

/**
 * Show error toast for entity creation
 */
export const showCreateError = (entity: string) => {
  showError(toastMessages.create.error(entity));
};

/**
 * Show success toast for entity update
 */
export const showUpdateSuccess = (entity: string) => {
  showSuccess(toastMessages.update.success(entity));
};

/**
 * Show error toast for entity update
 */
export const showUpdateError = (entity: string) => {
  showError(toastMessages.update.error(entity));
};

/**
 * Show success toast for entity deletion
 */
export const showDeleteSuccess = (entity: string) => {
  showSuccess(toastMessages.delete.success(entity));
};

/**
 * Show error toast for entity deletion
 */
export const showDeleteError = (entity: string) => {
  showError(toastMessages.delete.error(entity));
};

/**
 * Show success toast for save operation
 */
export const showSaveSuccess = () => {
  showSuccess(toastMessages.save.success);
};

/**
 * Show error toast for save operation
 */
export const showSaveError = () => {
  showError(toastMessages.save.error);
};

/**
 * Show success toast for export operation
 */
export const showExportSuccess = (count: number, entity: string) => {
  showSuccess(toastMessages.export.success(count, entity));
};

/**
 * Show error toast for export operation
 */
export const showExportError = (entity: string) => {
  showError(toastMessages.export.error(entity));
};

/**
 * Show copy to clipboard success
 */
export const showCopySuccess = () => {
  showSuccess(toastMessages.copy.success);
};

// ============================================
// STRIPE DECLINE MESSAGE MAPPING
// ============================================

export const stripeDeclineMessages: Record<string, string> = {
  card_declined: "Your card was declined. Please try a different card.",
  insufficient_funds:
    "Your card has insufficient funds. Please use a different card or add funds.",
  incorrect_cvc:
    "The security code (CVC) is incorrect. Please check and try again.",
  expired_card: "Your card has expired. Please use a different card.",
  processing_error:
    "A processing error occurred. Please try again in a moment.",
  incorrect_number:
    "The card number is incorrect. Please check and try again.",
  do_not_honor:
    "Your bank declined this transaction. Please contact your bank or try a different card.",
  generic_decline:
    "Your card was declined. Please try a different payment method.",
  authentication_required:
    "Additional authentication is required. Please try again and complete the verification step.",
  lost_card:
    "This card has been reported lost. Please use a different card.",
  stolen_card:
    "This card has been reported stolen. Please use a different card.",
  try_again_later:
    "The payment could not be processed right now. Please try again later.",
  withdrawal_count_limit_exceeded:
    "You have exceeded the number of allowed transactions. Please try again later or use a different card.",
  not_permitted:
    "This type of transaction is not permitted on this card. Please use a different card.",
  invalid_account:
    "The card or account information is invalid. Please check your details or use a different card.",
};

export function getFriendlyDeclineMessage(
  declineCode: string | null,
  fallback: string
): string {
  if (declineCode && stripeDeclineMessages[declineCode]) {
    return stripeDeclineMessages[declineCode];
  }
  return fallback;
}

/**
 * Promise-based toast for async operations
 * Automatically handles success/error states
 */
export const toastPromise = <T>(
  promise: Promise<T>,
  messages: {
    loading: string;
    success: string | ((data: T) => string);
    error: string | ((err: unknown) => string);
  }
) => {
  return toast.promise(promise, {
    loading: messages.loading,
    success: messages.success,
    error: messages.error,
  });
};
